import {
  Button,
  Box,
  Flex,
  useDisclosure,
  CModal,
  CModalBody,
  ModalCloseButton,
  ModalContent,
  CModalFooter,
  CModalHeader,
  ModalOverlay,
  useToast,
  InputGroup,
  InputLeftAddon,
} from '@televet/kibble-ui';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import React, { useCallback, useEffect, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useUpdateOneClinicPhoneNumberMutation } from 'shared/types/graphql';
import { cleanPhoneNumber, validatePhoneNumber } from 'shared/utils';

interface IEdtClinicPhoneNumberButtonProps {
  currentNumber: string;
  isPrimary: boolean;
  isVerified: boolean;
  clinicPhoneNumberId: string;
  setPhoneNumbersError: (error: string) => void;
}

const EditClinicPhoneNumberButton = ({
  currentNumber,
  isPrimary,
  isVerified,
  clinicPhoneNumberId,
  setPhoneNumbersError,
}: IEdtClinicPhoneNumberButtonProps): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [updateClinicPhoneNumberErrorMessage, setUpdateClinicPhoneNumberErrorMessage] = useState('');
  const [numberToUpdate, setNumberToUpdate] = useState(currentNumber.replace(/\+1/g, ''));

  const resetState = useCallback((): void => {
    setUpdateClinicPhoneNumberErrorMessage('');
    setNumberToUpdate(currentNumber.replace(/\+1/g, ''));
  }, [currentNumber]);

  const toast = useToast();

  const { currentClinicId } = useClinicUser();

  useEffect(() => {
    resetState();
  }, [currentClinicId, resetState]);

  const [updateClinicPhoneNumber] = useUpdateOneClinicPhoneNumberMutation();

  const handleEditPhoneNumberClick = async (): Promise<void> => {
    const phoneValidation = validatePhoneNumber(numberToUpdate);
    try {
      if (!phoneValidation) {
        setUpdateClinicPhoneNumberErrorMessage('Please enter a valid phone number');

        return;
      }
      await updateClinicPhoneNumber({
        variables: { where: { id: clinicPhoneNumberId }, data: { number: `+1${cleanPhoneNumber(numberToUpdate)}` } },
      });
      toast({
        title: 'Successful number edit.',
        status: 'success',
        duration: 2000,
        position: 'top',
      });
      resetState();
      onClose();
    } catch {
      setUpdateClinicPhoneNumberErrorMessage(
        'Unable to edit phone number, probably phone number with SMS capability already exists, please create a Zendesk ticket',
      );
    }
  };

  return (
    <>
      <CModal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <CModalHeader>Edit Phone Number</CModalHeader>
          <ModalCloseButton />
          <CModalBody>
            Please add a valid phone number (123)-456-7890
            <Flex justifyContent={'center'} marginTop="20px">
              <InputGroup width={'200px'}>
                <InputLeftAddon>+1</InputLeftAddon>
                <TextInput
                  type="tel"
                  value={numberToUpdate}
                  onChange={(event): void => {
                    setNumberToUpdate(event.currentTarget.value);
                  }}
                  placeholder="phone number"
                />
              </InputGroup>
            </Flex>
            {updateClinicPhoneNumberErrorMessage && (
              <Box marginTop={'10px'} color="text.danger">
                {updateClinicPhoneNumberErrorMessage}
              </Box>
            )}
          </CModalBody>

          <CModalFooter marginBottom={'8px'}>
            <Button onClick={handleEditPhoneNumberClick}>Save Number</Button>
          </CModalFooter>
        </ModalContent>
      </CModal>

      <Button
        size="sm"
        variant="ghostNeutral"
        aria-label="Edit phone number"
        iconName="pen"
        onClick={(): void => {
          if (!isPrimary && !isVerified) {
            onOpen();
          } else {
            setPhoneNumbersError('Unable to edit numbers that are active or verified.');
          }
        }}
      />
    </>
  );
};

export default EditClinicPhoneNumberButton;
